import * as Api from '@ViewModels';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import {
	invalidateInfiniteSurveys,
	invalidateSurveyById,
	useCreateSatisfactionSurveyMutation,
	useReorderSatisfactionSurveyFieldsMutation,
	useUpdateSatisfactionSurveyMutation,
} from '../../../../../queries';
import { FieldStateManager } from '../../shared/components/AdditionalQuestions/types';
import { patchFieldTypesFromServerResponse } from '../../shared/utils/patchFieldTypesFromServerResponse';
import { SatisfactionSurveyFormData } from '../utils';

interface UseSatisfactionSurveyMutationsProps {
	impersonationContext?: Api.IImpersonationContext;
	getValues: () => SatisfactionSurveyFormData;
	reset: UseFormReset<SatisfactionSurveyFormData>;
	setValue: UseFormSetValue<SatisfactionSurveyFormData>;
	surveyData?: Api.ISatisfactionSurvey;
	isClone: boolean;
	onReorderSuccess?: (fields: Api.IFormField<string>[]) => void;
	/** Lifted per-field dirty manager — cleared after a successful save. */
	fieldStateManager?: FieldStateManager;
}

// Hook for mutations - follows EventRegistrationSurvey and CustomSurvey patterns
export const useSatisfactionSurveyMutations = (props: UseSatisfactionSurveyMutationsProps) => {
	const { impersonationContext, getValues, reset, setValue, onReorderSuccess, fieldStateManager } = props;
	const history = useHistory();
	const { logApiError } = useEventLogging('SatisfactionSurvey');
	const errorMessages = useErrorMessages();

	// Create new survey - single API call handles everything
	const createSurveyMutation = useCreateSatisfactionSurveyMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to create satisfaction survey');
			logApiError('SatisfactionCreateSurvey-Error', error);
		},
		onSuccess: (val: Api.ISatisfactionSurvey) => {
			toast.success('Satisfaction survey created successfully');
			history.replace(`/surveys/satisfaction/edit/${val.id}`);
			invalidateInfiniteSurveys();
			invalidateSurveyById();
			// Patch `_type` onto locally-added fields so `hasBeenSaved` flips to true and the
			// "Not Saved" indicator clears. We deliberately do NOT replace the field array
			// wholesale — the save response strips `options` (those come back via the GET's
			// `?expand=options`), so a blanket overwrite wipes dropdown choices and breaks
			// any visibility rules that reference them.
			patchFieldTypesFromServerResponse(val?.customForm?.fields, getValues().customForm?.fields, setValue);
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
			fieldStateManager?.resetDirtyState();
			fieldStateManager?.updateInitialStates(formValues.customForm?.fields ?? []);
		},
	});

	// Update existing survey - single API call handles everything
	const updateSurveyMutation = useUpdateSatisfactionSurveyMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to update satisfaction survey');
			logApiError('SatisfactionUpdateSurvey-Error', error);
		},
		onSuccess: (data?: Api.ISatisfactionSurvey) => {
			toast.success('Satisfaction survey updated successfully');
			invalidateInfiniteSurveys();
			invalidateSurveyById();
			// See createSurveyMutation onSuccess for the patch-vs-overwrite rationale.
			patchFieldTypesFromServerResponse(data?.customForm?.fields, getValues().customForm?.fields, setValue);
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
			fieldStateManager?.resetDirtyState();
			fieldStateManager?.updateInitialStates(formValues.customForm?.fields ?? []);
		},
	});

	const reorderSurveyFieldsMutation = useReorderSatisfactionSurveyFieldsMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder satisfaction survey fields');
			logApiError('SatisfactionReorderSurveyFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Satisfaction survey fields reordered successfully');
			if (onReorderSuccess && data?.fields) {
				onReorderSuccess(data.fields);
			}
		},
	});

	return {
		createSurveyMutation,
		updateSurveyMutation,
		reorderSurveyFieldsMutation,
	};
};
